import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPinned, Search, ThumbsUp } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { getGPS, reverseGeocode } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLive } from "@/context/Realtime";
import CoinCard from "@/components/CoinCard";
import { UAvatar, Verified, Empty, FeaturedBadge } from "@/components/common";

export function LocationRow({ user, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const update = async () => {
    setBusy(true);
    try {
      const p = await getGPS();
      const label = await reverseGeocode(p.lat, p.lng);
      await api.put("/me/location", { ...p, label });
      await onUpdated();
      toast.success("Lokasi diperbarui");
    } catch (e) {
      toast.error(e.message || errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const online = !!user.location;
  return (
    <div data-testid="gps-location-badge" className="flex items-center gap-2 text-sm">
      <span className="truncate flex-1 text-white/90">{user.location?.label || "Lokasi belum diatur"}</span>
      <button data-testid="update-location-btn" onClick={update} disabled={busy} title={online ? "Maps online" : "Aktifkan lokasi"}
        className="relative flex items-center gap-1.5 text-[11px] font-bold bg-white/15 hover:bg-white/25 pl-2 pr-3 py-1.5 rounded-full transition-colors">
        <span className="relative w-6 h-6 rounded-full bg-white text-emerald-700 grid place-items-center">
          <MapPinned className={`w-3.5 h-3.5 ${busy ? "animate-bounce" : ""}`} />
          <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? "bg-emerald-400" : "bg-slate-400"}`} />
        </span>
        {busy ? "Mencari..." : online ? "Maps Online" : "Aktifkan"}
      </button>
    </div>
  );
}

export function WorkerCard({ w }) {
  return (
    <Link to={`/workers/${w.id}`} data-testid={`worker-card-${w.id}`}
      className="flex items-center gap-3 bg-white rounded-2xl border border-slate-200/80 p-3 hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform]">
      <div className="relative">
        <UAvatar path={w.avatar} name={w.name} className="w-14 h-14" />
        <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <p className="font-bold truncate">{w.name}</p>
          <Verified show={w.verified} />
          <FeaturedBadge show={w.featured} compact />
        </div>
        <p className="text-xs text-slate-500 truncate">{w.skills.join(" · ")}</p>
        <div className="flex items-center gap-3 mt-1 text-xs">
          <span className="font-mono font-bold text-emerald-700">{w.distance_km} km</span>
          <span className="flex items-center gap-1 text-slate-500"><ThumbsUp className="w-3 h-3" /> {w.thumbs_up}</span>
        </div>
      </div>
    </Link>
  );
}

export default function UserHome() {
  const { user, refreshUser, settings } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [workers, setWorkers] = useState(null);
  const [tick, setTick] = useState(0);
  const [radius, setRadius] = useState(() => Number(localStorage.getItem("ukerja_radius")) || 20);
  const pickRadius = (r) => { setRadius(r); localStorage.setItem("ukerja_radius", String(r)); };
  useLive((e) => {
    if (e.type === "workers" || e.type === "reconnect") setTick((t) => t + 1);
  });

  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams({ radius: String(radius) });
      if (q) params.set("q", q);
      if (cat) params.set("category", cat);
      api.get(`/workers/nearby?${params}`).then((r) => setWorkers(r.data)).catch(() => setWorkers([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, cat, radius, tick, user.location?.lat, user.location?.lng]);

  return (
    <div data-testid="user-home-section" className="space-y-6">
      <CoinCard user={user}>
        <LocationRow user={user} onUpdated={refreshUser} />
      </CoinCard>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="worker-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cari worker terdekat atau keahlian..." className="field h-12 pl-11 rounded-full" />
      </div>

      <div data-testid="category-chip-list" className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
        {["", ...(settings.categories || [])].map((c) => (
          <button key={c || "all"} data-testid={`category-chip-${c || "all"}`} onClick={() => setCat(c)}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold border transition-colors ${cat === c ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
            {c || "Semua"}
          </button>
        ))}
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg">Worker online di sekitar</h2>
          <div data-testid="radius-toggle" className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
            {[10, 20].map((r) => (
              <button key={r} data-testid={`radius-${r}`} onClick={() => pickRadius(r)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${radius === r ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-emerald-700"}`}>{r} km</button>
            ))}
          </div>
        </div>
        {!user.location && <p className="text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2 mb-3">Aktifkan GPS agar kami bisa menemukan worker terdekat.</p>}
        <div data-testid="nearby-worker-list" className="space-y-3">
          {workers === null ? <div className="h-20 rounded-2xl bg-slate-200 animate-pulse" /> :
            workers.length === 0 ? <Empty title="Belum ada worker online" desc="Coba ubah kategori atau perbarui lokasi Anda." /> :
              workers.map((w) => <WorkerCard key={w.id} w={w} />)}
        </div>
      </section>
    </div>
  );
}
